import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { IHomeownerUser, IBusinessUser } from '@/domain/entities/user';
import { TypeNames } from '@/domain/shared/storable';
import { updateBusinessUserOrgCreation } from '@/features/users';
import { useAppDispatch } from '@/store/hooks';

type AdminUserDetailsCardProps = {
  user: IBusinessUser | IHomeownerUser | null;
  title?: string;
  description?: string;
  emptyMessage?: string;
  className?: string;
  canUpdateOrgCreation?: boolean;
};

function getDisplayName(user: IBusinessUser | IHomeownerUser | null) {
  if (!user) return '';
  const fullName = `${user.firstName?.trim?.() ?? ''} ${user.lastName?.trim?.() ?? ''}`.trim();
  return fullName || user.email || 'Unknown User';
}

function getDisplayInitials(user: IBusinessUser | IHomeownerUser | null) {
  if (!user) return '?';
  const first = user.firstName?.trim();
  const last = user.lastName?.trim();
  const initials = [first?.[0], last?.[0]].filter(Boolean).join('').toUpperCase();
  return initials || user.email?.slice(0, 2).toUpperCase() || '?';
}

function getDisplayPhone(user: IBusinessUser | IHomeownerUser | null) {
  if (!user) return '';
  if ('phone' in user && typeof user.phone === 'string') return user.phone;
  return '';
}

export function AdminUserDetailsCard({
  user,
  title = 'User details',
  description = 'Read-only profile. Targeted changes require a dedicated authorized admin endpoint.',
  emptyMessage = 'Select a user to inspect.',
  className,
  canUpdateOrgCreation = false,
}: AdminUserDetailsCardProps) {
  const dispatch = useAppDispatch();
  const [isSavingOrgCreation, setIsSavingOrgCreation] = useState(false);
  const fullName = getDisplayName(user);
  const initials = getDisplayInitials(user);

  const toggleOrgCreation = async () => {
    if (!user || user.typeName !== TypeNames.BusinessUser || !canUpdateOrgCreation || isSavingOrgCreation) return;
    const allowCreateOrg = !user.allowCreateOrg;
    setIsSavingOrgCreation(true);
    try {
      await dispatch(
        updateBusinessUserOrgCreation({ applicationUserId: user.applicationUserId, allowCreateOrg }),
      ).unwrap();
      toast.success(allowCreateOrg ? 'Organization creation enabled.' : 'Organization creation disabled.');
    } catch (err) {
      const error = err as { message?: string };
      toast.error(error.message || 'Failed to update organization creation permission');
    } finally {
      setIsSavingOrgCreation(false);
    }
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!user ? (
          <p className="text-muted-foreground text-sm">{emptyMessage}</p>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <div className="bg-muted flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{fullName}</p>
                <p className="text-muted-foreground truncate text-xs">{user.applicationUserId}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user.email ?? ''} readOnly />
              </div>

              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={getDisplayPhone(user)} readOnly />
              </div>
            </div>

            {user.typeName === TypeNames.BusinessUser ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Organization ID</Label>
                  <Input value={user.organizationID ?? ''} readOnly />
                </div>
                <div className="space-y-2">
                  <Label>Role ID</Label>
                  <Input value={user.roleId ?? ''} readOnly />
                </div>
                <div className="space-y-2">
                  <Label>Allow create organization</Label>
                  <Input value={user.allowCreateOrg ? 'Yes' : 'No'} readOnly />
                  {canUpdateOrgCreation ? (
                    <Button type="button" variant="outline" onClick={toggleOrgCreation} disabled={isSavingOrgCreation}>
                      {isSavingOrgCreation
                        ? 'Saving…'
                        : user.allowCreateOrg
                          ? 'Disable organization creation'
                          : 'Enable organization creation'}
                    </Button>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label>Phone verified</Label>
                  <Input value={user.phoneVerified ? 'Yes' : 'No'} readOnly />
                </div>
              </div>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Created</Label>
                <Input value={user.createdAt ? String(user.createdAt) : ''} readOnly />
              </div>
              <div className="space-y-2">
                <Label>Updated</Label>
                <Input value={user.updatedAt ? String(user.updatedAt) : ''} readOnly />
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
